import type { BreadcrumbItem } from '@/types/navigation';

export interface RouteConfig {
  path: string;
  label: string;
  parent?: string;
  dynamicSegments?: string[];
  children?: Record<string, RouteConfig>;
}

// Complete route configuration mapping
const ROUTE_CONFIG: Record<string, RouteConfig> = {
  // Main dashboard routes
  home: {
    path: '/home',
    label: 'Home',
  },
  dashboard: {
    path: '/dashboard',
    label: 'Dashboard',
  },
  chat: {
    path: '/chat',
    label: 'Chat',
  },

  // Code section
  'ask-ellie': {
    path: '/ask-ellie',
    label: 'Ask Ellie',
  },
  'world-model': {
    path: '/world-model',
    label: 'World Model',
  },
  'code-review': {
    path: '/code-review',
    label: 'Code Review',
  },
  documentation: {
    path: '/documentation',
    label: 'Knowledge Base',
    children: {
      intro: {
        path: '/documentation/[IntroArtifactUUID]',
        label: 'Knowledge Base',
        parent: 'documentation',
        dynamicSegments: ['IntroArtifactUUID'],
      },
      artifact: {
        path: '/documentation/[IntroArtifactUUID]/[ArtifactUUID]',
        label: 'Artifact',
        parent: 'documentation',
        dynamicSegments: ['IntroArtifactUUID', 'ArtifactUUID'],
      },
      v2: {
        path: '/documentation/[IntroArtifactUUID]/v2',
        label: 'Documentation v2',
        parent: 'documentation',
        dynamicSegments: ['IntroArtifactUUID'],
      },
    },
  },
  connectors: {
    path: '/connectors',
    label: 'Connectors',
  },
  boards: {
    path: '/boards',
    label: 'Boards',
    children: {
      detail: {
        path: '/boards/[boardId]',
        label: 'Board',
        parent: 'boards',
        dynamicSegments: ['boardId'],
      },
    },
  },
  'code-overview': {
    path: '/code-overview',
    label: 'Code Overview',
  },

  // Team section
  'team-overview': {
    path: '/team-overview',
    label: 'Team Overview',
    children: {
      team: {
        path: '/team-overview/[TeamUUID]',
        label: 'Team Details',
        parent: 'team-overview',
        dynamicSegments: ['TeamUUID'],
      },
    },
  },
  'team-insight': {
    path: '/team-insight',
    label: 'Team Insights',
    children: {
      user: {
        path: '/team-insight/[userid]',
        label: 'User Profile',
        parent: 'team-insight',
        dynamicSegments: ['userid'],
      },
    },
  },
  'agent-insights': {
    path: '/agent-insights',
    label: 'Agent Insights',
    children: {
      'optimization-engine': {
        path: '/agent-insights/optimization-engine',
        label: 'Optimization Engine',
        parent: 'agent-insights',
      },
    },
  },
  'model-router': {
    path: '/model-router',
    label: 'Model Router',
  },
  'model-quality': {
    path: '/model-quality',
    label: 'Model Quality',
  },
  'code-quality': {
    path: '/code-quality',
    label: 'Code Quality',
  },
  'deployment-health': {
    path: '/deployment-health',
    label: 'Deployment Health',
  },
  security: {
    path: '/security',
    label: 'Code Security',
    children: {
      dashboard: {
        path: '/security/dashboard',
        label: 'Security Dashboard',
        parent: 'security',
      },
      scan: {
        path: '/security/dashboard/[scan-id]',
        label: 'Security Scan',
        parent: 'security',
        dynamicSegments: ['scan-id'],
      },
    },
  },
  'individual-insights': {
    path: '/individual-insights',
    label: 'Individual Insights',
    children: {
      review: {
        path: '/individual-insights/[id]',
        label: 'Individual Insights',
        parent: 'individual-insights',
        dynamicSegments: ['id'],
      },
      view: {
        path: '/individual-insights/view',
        label: 'View Reviews',
        parent: 'individual-insights',
      },
      'view-detail': {
        path: '/individual-insights/view/[id]',
        label: 'Review Details',
        parent: 'individual-insights',
        dynamicSegments: ['id'],
      },
      sample: {
        path: '/individual-insights/sample',
        label: 'Sample Review',
        parent: 'individual-insights',
      },
      'individual-insights-report': {
        path: '/individual-insights/report',
        label: 'Performance Review Report',
        parent: 'individual-insights',
      },
      report: {
        path: '/individual-insights/report/[reportId]',
        label: '',
        parent: 'individual-insights',
        dynamicSegments: ['reportId'],
      },
    },
  },
  leaderboard: {
    path: '/leaderboard',
    label: 'Leaderboard',
    children: {
      org: {
        path: '/leaderboard/[orgUUID]',
        label: 'Organization Leaderboard',
        parent: 'leaderboard',
        dynamicSegments: ['orgUUID'],
      },
    },
  },

  // Analytics & Performance
  analytics: {
    path: '/analytics',
    label: 'Analytics',
    children: {
      dashboard: {
        path: '/analytics/dashboard',
        label: 'Analytics Dashboard',
        parent: 'analytics',
      },
      user: {
        path: '/analytics/[userid]',
        label: 'User Analytics',
        parent: 'analytics',
        dynamicSegments: ['userid'],
      },
    },
  },
  performance: {
    path: '/performance',
    label: 'Performance',
  },

  // Management
  manage: {
    path: '/manage',
    label: 'Management',
    children: {
      api: {
        path: '/manage/api',
        label: 'API Management',
        parent: 'manage',
      },
      billing: {
        path: '/manage/billing',
        label: 'Billing',
        parent: 'manage',
      },
      integrations: {
        path: '/manage/integrations',
        label: 'Integrations',
        parent: 'manage',
      },
      mcp: {
        path: '/manage/mcp',
        label: 'MCP',
        parent: 'manage',
      },
      users: {
        path: '/manage/users',
        label: 'Users',
        parent: 'manage',
      },
    },
  },

  // Settings
  settings: {
    path: '/settings',
    label: 'Settings',
  },

  // CLI manual
  cli: {
    path: '/cli',
    label: 'CLI Guide',
  },

  // Other routes
  'pr-setup': {
    path: '/pr-setup',
    label: 'PR Setup',
  },
  'teaminsights-setup': {
    path: '/teaminsights-setup',
    label: 'Team Insights Setup',
  },
  automations: {
    path: '/automations',
    label: 'Automations',
  },
  pulse: {
    path: '/pulse',
    label: 'Pulse',
  },
  reactors: {
    path: '/reactors',
    label: 'Reactors',
  },
  'background-agents': {
    path: '/background-agents',
    label: 'Background Agents',
    children: {
      thread: {
        path: '/background-agents/[thread_id]',
        label: 'Agent Thread',
        parent: 'background-agents',
        dynamicSegments: ['thread_id'],
      },
      threads: {
        path: '/background-agents/threads',
        label: 'Agent Threads',
        parent: 'background-agents',
      },
    },
  },
  // Code Reliability consolidates Incident Knowledge + Observability into one
  // route with tabs (?tab=incidents|observability). The two feature routes below
  // stay registered for their repo deep-links (which remain live).
  'code-reliability': {
    path: '/code-reliability',
    label: 'Code Reliability',
  },

  // Repository-specific routes
  'incident-knowledge': {
    path: '/incident-knowledge',
    label: 'Incident Knowledge',
    children: {
      repos: {
        path: '/incident-knowledge/[repos]',
        label: 'Repository',
        parent: 'incident-knowledge',
        dynamicSegments: ['repos'],
      },
    },
  },
  'observability-audit': {
    path: '/observability-audit',
    label: 'Observability',
    children: {
      repo: {
        path: '/observability-audit/[repo]',
        label: 'Repository',
        parent: 'observability-audit',
        dynamicSegments: ['repo'],
      },
    },
  },
};

/**
 * Generates breadcrumb navigation for a given pathname
 * @param pathname - The current pathname (e.g., '/analytics/dashboard')
 * @param dynamicValues - Optional object with values for dynamic segments
 * @returns Array of breadcrumb items
 */
export function generateBreadcrumbs(
  pathname: string,
  dynamicValues?: Record<string, string>
): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];

  // Always start with Home
  breadcrumbs.push({
    label: 'Home',
    href: '/home',
    isActive: pathname === '/home',
  });

  if (pathname === '/home' || pathname === '/') {
    return breadcrumbs;
  }

  // Build breadcrumbs by matching segments with route patterns
  let parentBreadcrumbs: BreadcrumbItem[] = [];
  const matchedRoute = findMatchingRoute(segments, dynamicValues);

  if (matchedRoute) {
    parentBreadcrumbs = buildRouteHierarchy(
      matchedRoute,
      segments,
      dynamicValues
    );
  } else {
    // Fallback: build incrementally for unmatched routes
    parentBreadcrumbs = buildFallbackBreadcrumbs(segments, dynamicValues);
  }

  return [...breadcrumbs, ...parentBreadcrumbs];
}

/**
 * Finds a matching route configuration for the given segments
 */
function findMatchingRoute(
  segments: string[],
  dynamicValues?: Record<string, string>
): { config: RouteConfig; path: string } | undefined {
  // Start with the first segment
  if (segments.length === 0) return undefined;

  const firstSegment = segments[0];
  const config = ROUTE_CONFIG[firstSegment];

  if (!config) return undefined;

  let currentPath = `/${firstSegment}`;
  let currentConfig = config;

  // Navigate through remaining segments
  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];

    if (!currentConfig.children) {
      // No more children, this might be an unmatched route
      return undefined;
    }

    // Look for exact match first (static route key equals URL segment)
    if (currentConfig.children[segment]) {
      currentConfig = currentConfig.children[segment];
      currentPath += `/${segment}`;
      continue;
    }

    const childEntries = Object.entries(currentConfig.children);
    const dynamicChildren = childEntries.filter(
      ([, c]) => !!c.dynamicSegments?.length
    );
    const staticChildKeys = new Set(
      childEntries.filter(([, c]) => !c.dynamicSegments?.length).map(([k]) => k)
    );

    let foundDynamicMatch = false;

    // One catch-all dynamic child (e.g. /incident-knowledge/[repos]): any non-static segment matches
    if (dynamicChildren.length === 1 && !staticChildKeys.has(segment)) {
      currentConfig = dynamicChildren[0][1];
      currentPath += `/${segment}`;
      foundDynamicMatch = true;
    } else {
      for (const [, childConfig] of childEntries) {
        if (
          childConfig.dynamicSegments?.some(() => {
            return (
              isUUID(segment) ||
              Boolean(dynamicValues && dynamicValues[segment])
            );
          })
        ) {
          currentConfig = childConfig;
          currentPath += `/${segment}`;
          foundDynamicMatch = true;
          break;
        }
      }
    }

    if (!foundDynamicMatch) {
      return undefined;
    }
  }

  return { config: currentConfig, path: currentPath };
}

/**
 * Builds route hierarchy for breadcrumbs
 */
function buildRouteHierarchy(
  matchedRoute: { config: RouteConfig; path: string },
  segments: string[],
  dynamicValues?: Record<string, string>
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [];
  const { config } = matchedRoute;

  // Build hierarchy by walking up the parent chain
  const hierarchy = getRouteHierarchy(config, segments, dynamicValues);

  // Convert hierarchy to breadcrumbs
  for (let i = 0; i < hierarchy.length; i++) {
    const routeItem = hierarchy[i];
    const isLast = i === hierarchy.length - 1;

    breadcrumbs.push({
      label: routeItem.label,
      href: routeItem.href,
      isActive: isLast,
    });
  }

  return breadcrumbs;
}

/**
 * Gets the route hierarchy from root to current route
 */
function getRouteHierarchy(
  config: RouteConfig,
  segments: string[],
  dynamicValues?: Record<string, string>
): Array<{ label: string; href: string }> {
  const hierarchy: Array<{ label: string; href: string }> = [];

  // Find the root config and build path
  let currentPath = '';

  // Handle root-level routes
  if (!config.parent) {
    const segment = segments[0];
    const label = (dynamicValues && dynamicValues[segment]) || config.label;
    currentPath = `/${segment}`;

    hierarchy.push({
      label,
      href: currentPath,
    });

    // Add child routes if they exist
    for (let i = 1; i < segments.length; i++) {
      const segment = segments[i];
      currentPath += `/${segment}`;

      const childConfig = findChildConfigForSegment(
        config,
        segment,
        i,
        segments
      );
      const label =
        (dynamicValues && dynamicValues[segment]) ||
        childConfig?.label ||
        formatSegmentLabel(segment);

      hierarchy.push({
        label,
        href: currentPath,
      });

      if (childConfig) {
        config = childConfig;
      }
    }
  } else {
    // Handle nested routes - need to build from parent
    const parentHierarchy = buildParentHierarchy(
      config,
      segments,
      dynamicValues
    );
    hierarchy.push(...parentHierarchy);
  }

  return hierarchy;
}

/**
 * Builds parent hierarchy for nested routes
 */
function buildParentHierarchy(
  config: RouteConfig,
  segments: string[],
  dynamicValues?: Record<string, string>
): Array<{ label: string; href: string }> {
  const hierarchy: Array<{ label: string; href: string }> = [];

  // Find parent config
  const parentKey = config.parent;
  if (parentKey) {
    const parentConfig = ROUTE_CONFIG[parentKey];
    if (parentConfig) {
      // Add parent first
      hierarchy.push({
        label: parentConfig.label,
        href: parentConfig.path.replace(/\[.*?\]/g, (match) => {
          const paramName = match.slice(1, -1);
          // Find the corresponding segment value
          const segmentIndex = segments.findIndex(
            (seg) => dynamicValues?.[seg] || isUUID(seg)
          );
          return segmentIndex >= 0 ? segments[segmentIndex] : match;
        }),
      });
    }
  }

  // Add current route
  const currentHref = buildHrefFromPattern(
    config.path,
    segments,
    dynamicValues
  );
  const currentLabel = getCurrentRouteLabel(config, segments, dynamicValues);

  hierarchy.push({
    label: currentLabel,
    href: currentHref,
  });

  return hierarchy;
}

/**
 * Finds child config for a specific segment
 */
function findChildConfigForSegment(
  parentConfig: RouteConfig,
  segment: string,
  _index: number,
  _segments: string[]
): RouteConfig | undefined {
  if (!parentConfig.children) return undefined;

  if (parentConfig.children[segment]) {
    return parentConfig.children[segment];
  }

  const childEntries = Object.entries(parentConfig.children);
  const dynamicChildren = childEntries.filter(
    ([, c]) => !!c.dynamicSegments?.length
  );
  const staticChildKeys = new Set(
    childEntries.filter(([, c]) => !c.dynamicSegments?.length).map(([k]) => k)
  );

  if (dynamicChildren.length === 1 && !staticChildKeys.has(segment)) {
    return dynamicChildren[0][1];
  }

  for (const [, childConfig] of childEntries) {
    if (childConfig.dynamicSegments && isUUID(segment)) {
      return childConfig;
    }
  }

  return undefined;
}

/**
 * Builds href from route pattern and actual segments
 */
function buildHrefFromPattern(
  pattern: string,
  segments: string[],
  _dynamicValues?: Record<string, string>
): string {
  const patternParts = pattern.split('/').filter(Boolean);
  if (patternParts.length !== segments.length) {
    return `/${segments.join('/')}`;
  }
  const out: string[] = [];
  for (let i = 0; i < patternParts.length; i++) {
    const p = patternParts[i];
    const actual = segments[i];
    if (p.startsWith('[') && p.endsWith(']')) {
      out.push(actual);
    } else if (p === actual) {
      out.push(actual);
    } else {
      return `/${segments.join('/')}`;
    }
  }
  return `/${out.join('/')}`;
}

/**
 * Gets current route label with dynamic value resolution
 */
function getCurrentRouteLabel(
  config: RouteConfig,
  segments: string[],
  dynamicValues?: Record<string, string>
): string {
  const last = segments[segments.length - 1];
  const paramNames = config.dynamicSegments;
  if (paramNames?.length && last) {
    for (const name of paramNames) {
      if (dynamicValues?.[name]) {
        return dynamicValues[name]!;
      }
    }
  }

  for (const segment of segments) {
    if (dynamicValues && dynamicValues[segment]) {
      return dynamicValues[segment];
    }
  }

  if (paramNames?.length && last) {
    if (isUUID(last)) {
      return config.label;
    }
    return formatSegmentLabel(last);
  }

  return config.label;
}

/**
 * Fallback breadcrumb builder for unmatched routes
 */
function buildFallbackBreadcrumbs(
  segments: string[],
  dynamicValues?: Record<string, string>
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [];
  let currentPath = '';

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    currentPath += `/${segment}`;
    const label =
      (dynamicValues && dynamicValues[segment]) || formatSegmentLabel(segment);

    breadcrumbs.push({
      label,
      href: currentPath,
      isActive: i === segments.length - 1,
    });
  }

  return breadcrumbs;
}

/**
 * Checks if a string is a UUID
 */
function isUUID(str: string): boolean {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * TODO: do not fall back to this which is turning
 * performance-review to Performance Review
 * in Individual Insights page breadcrumbs
 * Formats a segment into a readable label.
 * Decodes URL-encoded segments so e.g. %2F shows as / and %20 as space.
 */
function formatSegmentLabel(segment: string): string {
  let decoded: string;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    decoded = segment;
  }
  // If decoded contains slash (e.g. owner/repo), use as-is so "Entelligence AI/FE Main" displays correctly
  if (decoded.includes('/')) {
    return decoded;
  }
  return decoded
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Gets the page title for a given pathname
 * @param pathname - The current pathname
 * @param dynamicValues - Optional object with values for dynamic segments
 * @returns The page title
 */
export function getPageTitle(
  pathname: string,
  dynamicValues?: Record<string, string>
): string {
  const breadcrumbs = generateBreadcrumbs(pathname, dynamicValues);
  const activeBreadcrumb = breadcrumbs.find((b) => b.isActive);

  if (activeBreadcrumb) {
    return activeBreadcrumb.label;
  }

  // Fallback to last segment
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0) {
    return formatSegmentLabel(segments[segments.length - 1]);
  }

  return 'Dashboard';
}

/**
 * Gets the parent route for a given pathname
 * @param pathname - The current pathname
 * @returns The parent route path or null
 */
function getParentRoute(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length <= 1) {
    return null;
  }

  return '/' + segments.slice(0, -1).join('/');
}

/**
 * Checks if a route is a child of another route
 * @param childPath - The child route path
 * @param parentPath - The parent route path
 * @returns True if child is a descendant of parent
 */
function isChildRoute(childPath: string, parentPath: string): boolean {
  return childPath.startsWith(parentPath) && childPath !== parentPath;
}

/**
 * Gets all available routes (useful for navigation menus)
 * @returns Array of all route configurations
 */
function getAllRoutes(): RouteConfig[] {
  return Object.values(ROUTE_CONFIG);
}

/**
 * Gets routes for a specific section
 * @param section - The section name (e.g., 'CODE', 'TEAM')
 * @returns Array of route configurations for the section
 */
function getRoutesBySection(section: string): RouteConfig[] {
  // This would need to be extended based on how sections are defined
  // For now, returning all routes
  return Object.values(ROUTE_CONFIG);
}
